import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { table6_1 } from '@/mocks/tableData';
import { calculatePF } from '@/utils/calculator';

const hcValues = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6];

interface ChartDataPoint {
  ts: number;
  [key: string]: number;
}

export default function PFChart() {
  const [activeHc, setActiveHc] = useState<number[]>([0.2, 0.4, 0.6]);

  const data = useMemo<ChartDataPoint[]>(() => {
    return table6_1.map((point) => {
      const row: ChartDataPoint = { ts: point.ts };
      hcValues.forEach((hc) => {
        const result = calculatePF(point.ts, hc);
        row[`hc_${hc}`] = result.pf;
      });
      return row;
    });
  }, []);

  const colors: Record<string, string> = {
    'hc_0.1': '#10b981',
    'hc_0.2': '#34d399',
    'hc_0.3': '#22c55e',
    'hc_0.4': '#059669',
    'hc_0.5': '#047857',
    'hc_0.6': '#065f46',
  };

  const labels: Record<string, string> = {
    'hc_0.1': 'H/C_org = 0.1',
    'hc_0.2': 'H/C_org = 0.2',
    'hc_0.3': 'H/C_org = 0.3',
    'hc_0.4': 'H/C_org = 0.4',
    'hc_0.5': 'H/C_org = 0.5',
    'hc_0.6': 'H/C_org = 0.6',
  };

  const toggleHc = (hc: number) => {
    setActiveHc((prev) => {
      if (prev.includes(hc)) {
        if (prev.length <= 1) return prev;
        return prev.filter((v) => v !== hc);
      }
      return [...prev, hc];
    });
  };

  return (
    <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-primary-500/15 flex items-center justify-center">
          <i className="ri-line-chart-line text-primary-600 text-xl" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">PF vs 土壤温度</h2>
          <p className="text-sm text-foreground-600 mt-0.5">不同 H/C_org 值下的持久系数变化曲线</p>
        </div>
      </div>

      {/* Legend toggles */}
      <div className="flex flex-wrap gap-2 mb-6 mt-4">
        {hcValues.map((hc) => {
          const isActive = activeHc.includes(hc);
          const key = `hc_${hc}`;
          return (
            <button
              key={hc}
              onClick={() => toggleHc(hc)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-background-100 border border-background-300 shadow-sm'
                  : 'bg-background-100/50 border border-background-200/50 opacity-50'
              }`}
            >
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colors[key] }}
              />
              <span className="text-foreground-800">{labels[key]}</span>
            </button>
          );
        })}
      </div>

      <div className="h-80 md:h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" />
            <XAxis
              dataKey="ts"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              label={{
                value: '土壤温度 Ts (°C)',
                position: 'insideBottom',
                offset: -5,
                style: { fontSize: 12, fill: '#6b7280' },
              }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickLine={{ stroke: '#d1d5db' }}
              label={{
                value: 'PF (%)',
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 12, fill: '#6b7280' },
              }}
              domain={[0, 'auto']}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                fontSize: '13px',
              }}
              labelStyle={{ color: '#374151', fontWeight: 600 }}
            />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              formatter={(value: string) => labels[value] || value}
            />
            <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
            {hcValues.map((hc) => {
              const key = `hc_${hc}`;
              if (!activeHc.includes(hc)) return null;
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={colors[key]}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: colors[key], strokeWidth: 0 }}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
                  name={key}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-foreground-500 mt-4 text-center">
        基于 Table 6.1 回归参数（7–40°C 范围内线性插值）。温度升高时 PF 下降，意味着碳持久性降低。
      </p>
    </div>
  );
}